// frontend/src/components/ContactRequestsList.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  CheckCircleIcon as CheckCircle2,
  CircleIcon as Circle,
  MailIcon as Mail,
  CalendarIcon as Calendar,
  MessageIcon as MessageSquare,
  UserIcon,
  LoaderIcon as Loader2,
  CheckCircleIcon as CheckCircle,
  SendIcon
} from './Icons';

const ContactRequestsList = () => {
  const { user, API_URL } = useContext(AuthContext);
  const { organization } = useSettings();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [successMessage, setSuccessMessage] = useState('');

  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    const fetchRequests = async () => {
      if (!user || (!user.token || (user.role !== 'admin' && user.role !== 'gruppenleiter'))) {
        setError('Nicht autorisiert. Sie müssen Admin oder Gruppenleiter sein.');
        setLoading(false);
        return;
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const res = await axios.get(`${API_URL}/contact`, config);
        setRequests(res.data);
        setLoading(false);
      } catch (err) {
        console.error('Fehler beim Laden der Kontaktanfragen:', err);
        setError('Fehler beim Laden der Kontaktanfragen.');
        setLoading(false);
      }
    };
    fetchRequests();
  }, [user, API_URL]);

  const handleToggleResolved = async (id, currentStatus) => {
    setSuccessMessage('');
    setError(null);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };
      await axios.put(`${API_URL}/contact/${id}`, { isResolved: !currentStatus }, config);
      setRequests(requests.map(req =>
        req._id === id ? { ...req, isResolved: !currentStatus } : req
      ));
      setSuccessMessage(`Anzeige aktualisiert: als ${!currentStatus ? 'gelöst' : 'ungelöst'} markiert.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Anfrage:', err);
      setError('Fehler beim Aktualisieren der Anfrage.');
    }
  };

  const openReplyModal = (request) => {
    setSelectedRequest(request);
    setReplySubject(`AW: ${request.subject}`);
    setReplyMessage(`Hallo ${request.name},\n\nvielen Dank für deine Nachricht.\n\nMit freundlichen Grüßen,\n${organization?.stammName || 'Pfadfinder'}`);
    setReplyModalOpen(true);
  };

  const closeReplyModal = () => {
    setReplyModalOpen(false);
    setSelectedRequest(null);
    setReplySubject('');
    setReplyMessage('');
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    setSendingReply(true);
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };

      const response = await axios.post(`${API_URL}/contact/${selectedRequest._id}/reply`, {
        subject: replySubject,
        message: replyMessage
      }, config);

      setSuccessMessage('Antwort erfolgreich gesendet!');
      if (response.data.pushSent) {
        setSuccessMessage(prev => prev + ' (Push-Benachrichtigung gesendet)');
      }

      closeReplyModal();
      setTimeout(() => setSuccessMessage(''), 5000);

    } catch (err) {
      console.error('Fehler beim Senden der Antwort:', err);
      setError(err.response?.data?.message || 'Fehler beim Senden der Antwort.');
    } finally {
      setSendingReply(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 animate-fade-in text-surface-onVariant">
      <Loader2 sx={{ fontSize: 40 }} className="animate-spin text-primary mb-4" />
      <p className="font-medium">Kontaktanfragen laden...</p>
    </div>
  );

  if (error) return (
    <div className="bg-error-container text-error-on-container p-6 rounded-2xl flex items-center gap-3 shadow-sm border border-error/10 animate-fade-in">
      <span className="text-xl">⚠️</span>
      <span className="font-bold">{error}</span>
    </div>
  );

  return (
    <div className="w-full space-y-6 animate-fade-in">
      {successMessage && (
        <div className="bg-primary-container text-primary-onContainer p-4 rounded-xl flex items-center gap-3 animate-fade-in border border-primary/10 shadow-sm">
          <CheckCircle2 sx={{ fontSize: 20 }} />
          <span className="font-bold uppercase tracking-wider text-sm">{successMessage}</span>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="text-center py-20 bg-surface-containerLowest rounded-3xl border-2 border-dashed border-outlineVariant">
          <div className="text-6xl mb-4 opacity-10">✉️</div>
          <p className="text-surface-onVariant font-bold tracking-wide italic">Keine neuen Kontaktanfragen vorhanden.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {requests.map(request => (
            <div
              key={request._id}
              className={`md3-card-elevated overflow-hidden border-l-8 transition-all duration-300 group ${request.isResolved
                ? 'bg-surface-containerLow border-outline opacity-80 grayscale-[0.3]'
                : 'bg-surface-container border-primary shadow-elevation-2'
                }`}
            >
              <div className="p-8">
                <div className="flex flex-col lg:flex-row justify-between gap-8">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${request.isResolved ? 'bg-surface-containerHighest text-surface-onVariant' : 'bg-primary-container text-primary-onContainer'} shadow-xs`}>
                          <UserIcon sx={{ fontSize: 24 }} />
                        </div>
                        <h3 className="text-headline-small font-bold text-surface-on tracking-tight">
                          {request.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-surface-containerHighest text-surface-onVariant border border-outlineVariant font-bold text-sm shadow-xs">
                        <Calendar sx={{ fontSize: 16 }} />
                        {new Date(request.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-6">
                      <a href={`mailto:${request.email}`} className="flex items-center gap-2 px-4 py-2 bg-secondary-container text-secondary-onContainer rounded-2xl hover:brightness-95 transition-all font-bold text-sm border border-secondary/20 shadow-xs">
                        <Mail sx={{ fontSize: 18 }} />
                        {request.email}
                      </a>
                      <div className="flex items-center gap-2 px-4 py-2 bg-surface-containerHighest text-surface-on rounded-2xl font-bold text-sm border border-outlineVariant">
                        <span className="text-surface-onVariant uppercase tracking-widest text-[10px] mr-1">Betreff:</span>
                        {request.subject}
                      </div>
                    </div>

                    <div className="bg-surface-containerLowest p-6 rounded-2xl border border-outlineVariant shadow-inner relative">
                      <div className="absolute -top-3 left-4 bg-surface-containerLowest px-2 text-[10px] font-black tracking-widest text-surface-onVariant uppercase italic">Nachricht</div>
                      <div className="flex gap-4">
                        <MessageSquare sx={{ fontSize: 20 }} className="text-primary/40 shrink-0 mt-1" />
                        <p className="text-body-large text-surface-on whitespace-pre-wrap leading-relaxed font-medium">
                          {request.message}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center min-w-[240px] gap-4 bg-surface-on/[0.03] p-6 rounded-3xl border border-outlineVariant/50">
                    <div className="text-center mb-2">
                      <span className={`inline-flex items-center gap-2 px-6 py-2 rounded-full text-sm font-black uppercase tracking-tighter shadow-sm transition-all ${request.isResolved
                        ? 'bg-outline text-surface-on border border-outlineVariant shadow-inner'
                        : 'bg-tertiary text-tertiary-on border-4 border-tertiary-container ring-4 ring-tertiary/10'
                        }`}>
                        {request.isResolved ? "Erledigt" : "Offen / Neu"}
                      </span>
                    </div>

                    <button
                      onClick={() => handleToggleResolved(request._id, request.isResolved)}
                      className={`w-full h-14 rounded-2xl flex items-center justify-center gap-3 font-bold transition-all shadow-elevation-1 hover:shadow-elevation-2 active:scale-95 border-b-4 ${request.isResolved
                        ? 'bg-surface-containerHighest text-surface-on border-outline hover:bg-surface-variant'
                        : 'bg-primary text-primary-on border-primary-dark hover:brightness-110'
                        }`}
                    >
                      {request.isResolved ? <Circle sx={{ fontSize: 24 }} /> : <CheckCircle sx={{ fontSize: 24 }} />}
                      {request.isResolved ? 'Als offen markieren' : 'Als gelöst markieren'}
                    </button>

                    {!request.isResolved && (
                      <p className="text-[10px] text-center text-surface-onVariant font-bold uppercase tracking-widest opacity-60">Handlung erforderlich</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 mt-4 self-center lg:self-start min-w-[240px]">
                    <button
                      onClick={() => openReplyModal(request)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-secondary-container text-secondary-onContainer rounded-xl font-bold hover:brightness-95 transition-all text-sm border border-secondary/20 shadow-xs w-full"
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                      Antworten
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply Modal */}
      {replyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface-containerHigh text-surface-on p-6 rounded-3xl w-full max-w-2xl shadow-elevation-3 border border-outlineVariant flex flex-col max-h-[90vh]">
            <h3 className="text-headline-small font-bold mb-4">Antworten an {selectedRequest?.name}</h3>

            <form onSubmit={handleSendReply} className="flex flex-col gap-4 overflow-y-auto pr-2">
              <div>
                <label className="text-sm font-bold text-surface-onVariant uppercase tracking-wider mb-1 block">Betreff</label>
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  className="w-full bg-surface-containerHighest text-surface-on p-3 rounded-xl border border-outline focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-bold text-surface-onVariant uppercase tracking-wider mb-1 block">Nachricht</label>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={10}
                  className="w-full bg-surface-containerHighest text-surface-on p-3 rounded-xl border border-outline focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-medium resize-none"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-outlineVariant">
                <button
                  type="button"
                  onClick={closeReplyModal}
                  className="px-6 py-3 rounded-xl font-bold hover:bg-surface-containerHighest transition-colors text-surface-onVariant"
                  disabled={sendingReply}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={sendingReply}
                  className="px-6 py-3 bg-primary text-primary-on rounded-xl font-bold shadow-lg hover:shadow-xl hover:brightness-110 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {sendingReply ? (
                    <><Loader2 sx={{ fontSize: 18 }} className="animate-spin" /> Sende...</>
                  ) : (
                    <><SendIcon sx={{ fontSize: 18 }} /> Senden</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactRequestsList;
